// All Releases-page API calls. Each function maps to one backend endpoint and
// goes through the shared apiService (never raw axios/fetch). The backend wraps
// every response in ResponseModel ({ data, message }); `unwrap` pulls the data.

import { apiService } from "src/services";
import {
	ICycleCreatePayload,
	ICyclesPayload,
	ICycleUpdatePayload,
	IPlanEmailPayload,
	IPlanEmailPreview,
	IPlanEmailResult,
	IRelease,
	IReleaseCompleteResult,
	IReleaseCreatePayload,
	IReleaseCycle,
	IReleasesQuery,
	IReleaseUpdatePayload,
	IReleaseVersions,
} from "../models/interfaces";
import { IEnvelope, unwrap, apiErrorMessage } from "src/utils/kcc";

// Re-exported so consumers keep a single import site for error formatting.
export { apiErrorMessage };

// GET /releases/ — releases in a date range (or all when no range is given);
// cancelled excluded by default. Trailing slash matches the backend collection
// route exactly (avoids a 307 redirect under CORS, same as /tenants/).
export const getReleases = async (query: IReleasesQuery = {}): Promise<IRelease[]> => {
	const params: Record<string, unknown> = { include_cancelled: query.include_cancelled ?? false };
	if (query.start) params.start = query.start;
	if (query.end) params.end = query.end;
	const res = await apiService.get<IEnvelope<IRelease[]>>("/releases/", params);
	return unwrap<IRelease[]>(res) ?? [];
};

// GET /releases/versions — the CTPL secret snapshot: latest bundle + per-tenant
// deployed versions (TeamCity updates the secret on every deploy).
export const getReleaseVersions = async (): Promise<IReleaseVersions> => {
	const res = await apiService.get<IEnvelope<IReleaseVersions>>("/releases/versions");
	return unwrap<IReleaseVersions>(res);
};

// POST /releases/ — plan a release (409 = the tenant already has an open plan).
export const createRelease = async (payload: IReleaseCreatePayload): Promise<IRelease> => {
	const res = await apiService.post<IEnvelope<IRelease>, IReleaseCreatePayload>("/releases/", payload);
	return unwrap<IRelease>(res);
};

// PATCH /releases/{id} — move/edit an open plan (date, target bundle, notes).
export const updateRelease = async (id: string, payload: IReleaseUpdatePayload): Promise<IRelease> => {
	const res = await apiService.patch<IEnvelope<IRelease>, IReleaseUpdatePayload>(`/releases/${id}`, payload);
	return unwrap<IRelease>(res);
};

// POST /releases/{id}/complete — mark released. Returns the completed release,
// the Secrets Manager cross-check, and the drafted QA smoke-test email.
export const completeRelease = async (id: string): Promise<IReleaseCompleteResult> => {
	const res = await apiService.post<IEnvelope<IReleaseCompleteResult>, Record<string, never>>(
		`/releases/${id}/complete`,
		{},
	);
	return unwrap<IReleaseCompleteResult>(res);
};

// DELETE /releases/{id} — cancel a plan (soft delete on the backend).
export const cancelRelease = async (id: string): Promise<void> => {
	await apiService.delete(`/releases/${id}`);
};

// True when the backend rejected a create/move because the tenant already has
// an open planned release (HTTP 409).
export const isConflictError = (err: unknown): boolean =>
	(err as { response?: { status?: number } })?.response?.status === 409;

// POST /releases/plan/email — send the deployment-plan digest (branded HTML +
// calendar screenshot inline + CSV of today/upcoming releases) to the KST + QA
// distribution. The base64 PNG body is large, so allow a longer timeout.
export const emailDeploymentPlan = async (payload: IPlanEmailPayload): Promise<IPlanEmailResult> => {
	const res = await apiService.post<IEnvelope<IPlanEmailResult>, IPlanEmailPayload>(
		"/releases/plan/email",
		payload,
		{ timeout: 60000 },
	);
	return unwrap<IPlanEmailResult>(res);
};

// Same endpoint with dry_run=true — builds the digest and returns the resolved
// recipients + subject + body HTML WITHOUT sending, for the preview modal.
export const previewDeploymentPlan = async (
	payload: Omit<IPlanEmailPayload, "dry_run">,
): Promise<IPlanEmailPreview | IPlanEmailResult> => {
	const res = await apiService.post<IEnvelope<IPlanEmailPreview | IPlanEmailResult>, IPlanEmailPayload>(
		"/releases/plan/email",
		{ ...payload, dry_run: true },
		{ timeout: 60000 },
	);
	return unwrap<IPlanEmailPreview | IPlanEmailResult>(res);
};

// POST /releases/{id}/email — send the QA smoke-test email for a completed
// release from CTPL (2026-09-09; the copy/Outlook/.eml drafts are parked).
// The preview seeds from the complete-response's drafted email.
export const sendReleaseSmokeEmail = async (
	releaseId: string,
	payload: { to?: string[]; cc?: string[]; custom_subject?: string; custom_note?: string },
): Promise<{ sent: boolean; to: string[]; cc: string[] }> => {
	const res = await apiService.post<IEnvelope<{ sent: boolean; to: string[]; cc: string[] }>, typeof payload>(
		`/releases/${releaseId}/email`,
		payload,
		{ timeout: 60000 },
	);
	return unwrap(res);
};

// ── Dev timeline (release cycles) ────────────────────────────────────────────

// GET /releases/cycles — every train, fully derived (template + today + cycles
// with per-train tenant deployments). The FE renders this payload verbatim.
export const getReleaseCycles = async (): Promise<ICyclesPayload> => {
	const res = await apiService.get<IEnvelope<ICyclesPayload>>("/releases/cycles");
	return unwrap<ICyclesPayload>(res);
};

// POST /releases/cycles — create a train (409 = version already exists).
export const createReleaseCycle = async (payload: ICycleCreatePayload): Promise<IReleaseCycle> => {
	const res = await apiService.post<IEnvelope<IReleaseCycle>, ICycleCreatePayload>("/releases/cycles", payload);
	return unwrap<IReleaseCycle>(res);
};

// PATCH /releases/cycles/{version} — partial edit; returns the re-derived cycle.
export const updateReleaseCycle = async (version: string, payload: ICycleUpdatePayload): Promise<IReleaseCycle> => {
	const res = await apiService.patch<IEnvelope<IReleaseCycle>, ICycleUpdatePayload>(
		`/releases/cycles/${version}`,
		payload,
	);
	return unwrap<IReleaseCycle>(res);
};

// DELETE /releases/cycles/{version} — remove a train from the timeline.
export const deleteReleaseCycle = async (version: string): Promise<void> => {
	await apiService.delete(`/releases/cycles/${version}`);
};
